// ContractorMatchingStatus.tsx
import React, { useState, useEffect } from 'react';
import { subscribeToJobMatchingStatus } from './JobSubmissionService';

interface ContractorMatchingStatusProps {
  jobId: string;
  initialMatchingStatus?: string;
  initialMatchedCount?: number;
}

const ContractorMatchingStatus: React.FC<ContractorMatchingStatusProps> = ({
  jobId,
  initialMatchingStatus = 'pending',
  initialMatchedCount = 0
}) => {
  const [matchingStatus, setMatchingStatus] = useState(initialMatchingStatus);
  const [matchedContractorCount, setMatchedContractorCount] = useState(initialMatchedCount);

  useEffect(() => {
    if (!jobId) return;

    // Subscribe to real-time updates
    const unsubscribe = subscribeToJobMatchingStatus(
      jobId,
      (status: string, count?: number) => {
        setMatchingStatus(status);
        if (count !== undefined) {
          setMatchedContractorCount(count);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [jobId]);

  const renderMatchingStatus = () => {
    switch (matchingStatus) {
      case 'matching':
        return (
          <div className="d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <strong>📊 Contractor Matches: Currently matching...</strong>
          </div>
        );
      
      case 'completed':
        return (
          <div>
            <strong>📊 Contractor Matches: {matchedContractorCount}</strong>
            <div className="mt-2">
              {matchedContractorCount > 0 ? (
                <>
                  We found <strong>{matchedContractorCount} qualified contractors</strong> in your area who match your project requirements!
                  You can now go to the <strong>Hiring tab</strong> of your job to invite them to bid.
                </>
              ) : (
                'We\'re still finding qualified contractors for your project. Check back shortly to see matches.'
              )}
            </div>
          </div>
        );
      
      case 'error':
        return (
          <div className="text-warning">
            <strong>📊 Contractor Matches: Error occurred</strong>
            <div className="mt-2">
              There was an issue finding contractors. Our team has been notified and will resolve this shortly.
            </div>
          </div>
        );
      
      default: // pending
        return (
          <div>
            <strong>📊 Contractor Matches: Preparing to match...</strong>
          </div>
        );
    }
  };

  return (
    <div className="mt-3">
      {renderMatchingStatus()}
    </div>
  );
};

export default ContractorMatchingStatus;